import { ComboboxControl } from '@/components/controls/combobox-control';
import DatepickerControl from '@/components/controls/datepicker-control';
import { InputControl } from '@/components/controls/input-control';
import SelectControl from '@/components/controls/select-control';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface IFilterProps {
  date_from?: string;
  date_to?: string;
  farmer_tax_number?: string;
  organization_tax_number?: string;
  party_number?: string;
  place_number?: string;
  crop_type_id?: string;
  crop_generation_type?: string;
  harvest_type?: string;
  industrial_grade?: string;
  class?: string;
}

interface IProps {
  children: ReactNode;
  type: 'cargo_report' | 'party_report';
}

const ReportsFilterGrainModal: FC<IProps> = ({ children, type }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  const form = useForm<IFilterProps>({
    defaultValues: {
      date_from: searchParams.get('date_from') || '',
      date_to: searchParams.get('date_to') || '',
      farmer_tax_number: searchParams.get('farmer_tax_number') || '',
      organization_tax_number:
        searchParams.get('organization_tax_number') || '',
      party_number: searchParams.get('party_number') || '',
      place_number: searchParams.get('place_number') || '',
      crop_type_id: searchParams.get('crop_type_id') || '',
      crop_generation_type: searchParams.get('crop_generation_type') || '',
      harvest_type: searchParams.get('harvest_type') || '',
      industrial_grade: searchParams.get('industrial_grade') || '',
      class: searchParams.get('class') || '',
    },
    mode: 'onSubmit',
  });

  const { control, handleSubmit, reset } = form;

  const onOpenChange = (open: boolean) => {
    setOpen(open);
  };

  useEffect(() => {
    if (open) {
      reset({
        date_from: searchParams.get('date_from') || '',
        date_to: searchParams.get('date_to') || '',
        farmer_tax_number: searchParams.get('farmer_tax_number') || '',
        organization_tax_number:
          searchParams.get('organization_tax_number') || '',
        party_number: searchParams.get('party_number') || '',
        place_number: searchParams.get('place_number') || '',
        crop_type_id: searchParams.get('crop_type_id') || '',
        crop_generation_type: searchParams.get('crop_generation_type') || '',
        harvest_type: searchParams.get('harvest_type') || '',
        industrial_grade: searchParams.get('industrial_grade') || '',
        class: searchParams.get('class') || '',
      });
    }
  }, [open, reset, searchParams, type]);

  function removeEmptyValues(obj: Record<string, any>): Record<string, any> {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
  }

  const onSubmit = (values: IFilterProps) => {
    const cleanValues = removeEmptyValues(values);
    const newParams = new URLSearchParams();

    newParams.set('report_type', type);

    Object.entries(cleanValues).forEach(([key, value]) => {
      newParams.set(key, value.toString());
    });

    setSearchParams(newParams);
    onOpenChange(false);
  };

  const handleReset = () => {
    reset({
      date_from: '',
      date_to: '',
      farmer_tax_number: '',
      organization_tax_number: '',
      party_number: '',
      place_number: '',
      crop_type_id: '',
      crop_generation_type: '',
      harvest_type: '',
      industrial_grade: '',
      class: '',
    });
    setSearchParams({});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle></DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col space-y-4 w-full">
              <div className="grid grid-cols-3 gap-4">
                {type === 'cargo_report' && (
                  <>
                    <DatepickerControl
                      control={control}
                      name="date_from"
                      label={t('date.from')}
                    />
                    <DatepickerControl
                      control={control}
                      name="date_to"
                      label={t('date.to')}
                    />
                    <InputControl
                      control={control}
                      name="farmer_tax_number"
                      label={t('farmer.tax.number')}
                      isNumber
                      maxLength={9}
                    />
                    <InputControl
                      control={control}
                      name="organization_tax_number"
                      label={t('organization.stir.number')}
                      isNumber
                      maxLength={9}
                    />
                    <SelectControl
                      name="crop_generation_type"
                      label={t('selection.type.clan')}
                      options={[]}
                      control={control}
                    />
                    <SelectControl
                      options={[]}
                      control={control}
                      name="crop_type"
                      label={t('type.of.crop')}
                    />
                  </>
                )}
                {type === 'party_report' && (
                  <SelectControl
                    name="filter_type"
                    label={t('type.of.filter')}
                    options={[]}
                    control={control}
                  />
                )}
                <InputControl
                  control={control}
                  name="party_number"
                  label={t('party.number')}
                />
                {type === 'party_report' && (
                  <InputControl
                    control={control}
                    name="place_number"
                    label={t('place.number')}
                  />
                )}
                <ComboboxControl
                  name="crop_type_id"
                  label={t('selection.type')}
                  control={control}
                  options={[]}
                />

                <SelectControl
                  name="class"
                  label={t('class')}
                  options={[]}
                  control={control}
                />
                {type === 'party_report' && (
                  <>
                    <SelectControl
                      name="cluster_id"
                      label={t('claster')}
                      options={[]}
                      control={control}
                    />
                    <SelectControl
                      name="crop_type"
                      label={t('type.of.crop')}
                      options={[]}
                      control={control}
                    />
                  </>
                )}
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={handleReset}>
                {t('reset')}
              </Button>
              <Button type="submit">{t('apply')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportsFilterGrainModal;
